/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

'use client';

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import classNames from 'classnames';
import { AppProps } from 'next/app';
import { Noto_Sans_SC, Nunito, Oxygen_Mono, Roboto } from 'next/font/google';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../components/Layouts/AppLayout';
import { STORAGE_KEYS } from '../constants';
import '../dayjs-config';
import '../styles/globals.css';
import { init as initTranslations } from '../translations/i18n';
import { DatadogInit } from '../utils/datadog';
import { setOsanoLocale } from '../utils/osano';
import { getLocalStorageItem } from '../utils/storage';

interface Props {
  Component: AppProps['Component'] & { requireAuth?: boolean };
  pageProps: AppProps['pageProps'];
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      retry: 1,
    },
  },
});

function getStorage() {
  try {
    return sessionStorage;
  } catch {
    // Noop
  }
}

const localStoragePersister = createSyncStoragePersister({
  storage: getStorage(),
});

const nunitoSans = Nunito({
  subsets: ['latin'],
  variable: '--font-nunitoSans',
});

const notoSansSC = Noto_Sans_SC({
  subsets: ['latin'],
  variable: '--font-notoSansSC',
});

const oxygenMono = Oxygen_Mono({
  subsets: ['latin'],
  variable: '--font-oxygenMono',
  weight: '400',
});

const roboto = Roboto({
  subsets: ['latin'],
  variable: '--font-roboto',
});

initTranslations();

function App({ Component, pageProps }: Props) {
  const { i18n } = useTranslation();
  useEffect(() => {
    const storedLang = getLocalStorageItem(STORAGE_KEYS.LANGUAGE);
    const browserLang = typeof navigator !== 'undefined' ? navigator.language.split('-')[0] : 'en';
    const lang = storedLang || browserLang || 'en';

    if (i18n.language !== lang) {
      void i18n.changeLanguage(lang);
      setOsanoLocale(lang);
    }
  }, []);

  return (
    <div
      id='app'
      className={classNames(
        'flex w-full flex-col overflow-clip font-sans',
        nunitoSans.variable,
        notoSansSC.variable,
        oxygenMono.variable,
        roboto.variable,
      )}
      data-theme='dbd'
    >
      <DatadogInit />
      <PersistQueryClientProvider client={queryClient} persistOptions={{ persister: localStoragePersister }}>
        <AppLayout requireAuth={Component.requireAuth}>
          <Component {...pageProps} />
        </AppLayout>
      </PersistQueryClientProvider>
    </div>
  );
}

export default App;
